// EmailComposer template picker (Service Records polish #41).
//
// Loads the active communication_templates catalog (PR 19) when the
// composer opens, presents them as a popover next to the "AI help"
// button. Picking a template replaces the subject + body with the
// chosen template's content. Merge tokens (`{{primary_first_name}}`)
// stay literal — the v1 1:1 composer doesn't auto-resolve them so
// the advisor can confirm each substitution by hand. Future work
// (Phase 2 polish) plumbs the household/contact context through and
// pre-substitutes the household-stable fields server-side.
//
// Pure JSX; the consumer (EmailComposer) decides what to do with
// the picked template via the `onPick` callback.

import { useEffect, useState } from "react";

import {
  api,
  ApiError,
  type CommunicationTemplate,
} from "../../api";
import { Button, ErrorBanner } from "../../ui";
import { isPickableTemplate } from "@shared/esign/system-email-templates";

export { bodyTemplateToHtml } from "./template-picker-helpers";

export interface TemplatePickerProps {
  /** Render the popover open when true. */
  open: boolean;

  /** Called when the user closes the popover via the dismiss button
   *  or by picking a template. */
  onClose: () => void;

  /** Called when the user picks a template. The template is the
   *  raw row from listCommunicationTemplates — caller decides how
   *  to apply subject + body (replace? prepend?). */
  onPick: (template: CommunicationTemplate) => void;
}

export function TemplatePicker({
  open,
  onClose,
  onPick,
}: TemplatePickerProps) {
  const [templates, setTemplates] = useState<CommunicationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listCommunicationTemplates()
      .then((r) => {
        if (cancelled) return;
        // The OS's own emails (the signing invitation, PR 8f) carry tokens
        // only their sender fills in; they are edited in the library, never
        // picked here.
        setTemplates(r.templates.filter(isPickableTemplate));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setError(`${err.status} — ${err.message}`);
        } else if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Failed to load templates");
        }
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="template-picker"
      role="dialog"
      aria-label="Choose an email template"
    >
      <header className="template-picker__header">
        <strong>Choose a template</strong>
        <button
          type="button"
          className="template-picker__close"
          aria-label="Close template picker"
          onClick={onClose}
        >
          ×
        </button>
      </header>
      {loading && (
        <div className="template-picker__loading">Loading templates…</div>
      )}
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {!loading && !error && templates.length === 0 && (
        <p className="template-picker__empty">
          No active templates yet. Ask an admin to create one in
          /admin/communication-templates.
        </p>
      )}
      {!loading && !error && templates.length > 0 && (
        <ul className="template-picker__list">
          {templates.map((t) => (
            <li key={t.id} className="template-picker__item">
              <button
                type="button"
                className="template-picker__pick"
                onClick={() => {
                  onPick(t);
                  onClose();
                }}
              >
                <div className="template-picker__name">{t.display_name}</div>
                <div className="template-picker__meta">
                  <code className="template-picker__slug">{t.slug}</code>
                  {" · "}
                  <span className="template-picker__scope">{t.scope}</span>
                </div>
                {t.description && (
                  <div className="template-picker__desc">{t.description}</div>
                )}
                <div className="template-picker__preview">
                  {truncate(t.subject_template, 80)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      <footer className="template-picker__footer">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </footer>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1).trimEnd() + "…";
}

// bodyTemplateToHtml is exported above from template-picker-helpers.ts
// so the unit tests can pull it without the --jsx flag.
